var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var _a, _b, _c, _d, _e, _f, _g;
import { inject } from "tsyringe";
import { IdentifiableMap } from "../../collections";
import { makeImmutable } from "../../core";
import { perRestaurant } from "../../di";
import { ModifierInstanceFactory, CustomParameterInstanceFactory, } from "../ProductInstance";
import { FulfillmentInstructions, FulfillmentInstructionsToken, } from "../FulfillmentInstructions";
import { CreatorScopeProvider } from "./CreatorScopeProvider";
import { CustomParameterInstanceCreatorFactory } from "./CustomParameterInstanceFactory";
import { HalvesCreatorState } from "./HalvesCreatorState";
import { ModifierInstanceCreatorFactory } from "./ModifierInstanceCreatorFactory";
import { ProductInstanceCreatorPartFactory } from "./ProductInstanceCreatorPartFactory";
import { ProductTypePackagingFeeCreatorFactory } from "./ProductTypePackagingFeeCreatorFactory";
import { SinglePartCreatorState } from "./SinglePartCreatorState";
let ProductInstanceCreatorStateFactory = class ProductInstanceCreatorStateFactory {
    constructor(fulfillmentInstructions, modifierInstanceCreatorFactory, modifierInstanceFactory, customParameterInstanceCreatorFactory, customParameterInstanceFactory, productInstanceCreatorPartFactory, productTypePackagingFeeCreatorFactory) {
        this.fulfillmentInstructions = fulfillmentInstructions;
        this.modifierInstanceCreatorFactory = modifierInstanceCreatorFactory;
        this.modifierInstanceFactory = modifierInstanceFactory;
        this.customParameterInstanceCreatorFactory = customParameterInstanceCreatorFactory;
        this.customParameterInstanceFactory = customParameterInstanceFactory;
        this.productInstanceCreatorPartFactory = productInstanceCreatorPartFactory;
        this.productTypePackagingFeeCreatorFactory = productTypePackagingFeeCreatorFactory;
        makeImmutable(this);
    }
    fromProductInstanceParts(params) {
        switch (params.parts.type) {
            case "SinglePart":
                return this.createSingle({
                    productCategory: params.productCategory,
                    initialCommonModifierInstances: params.commonModifierInstances,
                    initialCommonCustomParameterInstances: params.commonCustomParameterInstances,
                    part: params.parts.part,
                    productPackagingFeeId: params.productPackagingFeeId,
                });
            case "Halves":
                return this.createHalves({
                    productCategory: params.productCategory,
                    initialCommonModifierInstances: params.commonModifierInstances,
                    initialCommonCustomParameterInstances: params.commonCustomParameterInstances,
                    firstHalf: params.parts.first,
                    secondHalf: params.parts.second,
                    productPackagingFeeId: params.productPackagingFeeId,
                });
        }
    }
    createSingle(params) {
        // Create custom parameter creators first
        const commonCustomParameterInstanceCreators = IdentifiableMap.fromIterable("typeId", params.part.productType.customParameterTypes.objects.map((customParameterType) => {
            const initialInstance = params.initialCommonCustomParameterInstances.find(customParameterType.id);
            return this.customParameterInstanceCreatorFactory.create(initialInstance !== null && initialInstance !== void 0 ? initialInstance : this.customParameterInstanceFactory.createWithDefaultChoice(customParameterType));
        }));
        // Create CreatorScopeProvider with isSplit=false
        const creatorScopeProvider = new CreatorScopeProvider(this.fulfillmentInstructions, false, commonCustomParameterInstanceCreators);
        // Now create modifier creators with the scope provider
        const commonModifierInstanceCreators = IdentifiableMap.fromIterable("typeId", params.part.productType.nonDivisibleModifierTypes.objects.map((modifierType) => {
            const initialInstance = params.initialCommonModifierInstances.find(modifierType.id);
            const modifierSettings = modifierType.defaultSettings;
            return this.modifierInstanceCreatorFactory.create({
                modifierInstance: initialInstance !== null && initialInstance !== void 0 ? initialInstance : this.modifierInstanceFactory.createWithDefaultItems({
                    modifierType,
                    modifierSettings,
                }),
                modifierSettings,
                creatorScopeProvider,
            });
        }));
        const partCreator = this.productInstanceCreatorPartFactory.create(params.part, creatorScopeProvider);
        const productTypePackagingFeeCreator = this.productTypePackagingFeeCreatorFactory.create(params.productPackagingFeeId);
        const state = new SinglePartCreatorState({
            productCategory: params.productCategory,
            commonModifierInstanceCreators,
            commonCustomParameterInstanceCreators,
            partCreator,
            creatorScopeProvider,
            productTypePackagingFeeCreator,
        });
        productTypePackagingFeeCreator.setProductInstance(state);
        return state;
    }
    createHalves(params) {
        // Create custom parameter creators first
        const commonCustomParameterInstanceCreators = IdentifiableMap.fromIterable("typeId", params.firstHalf.productType.customParameterTypes
            .union(params.secondHalf.productType.customParameterTypes)
            .objects.map((customParameterType) => {
            const initialInstance = params.initialCommonCustomParameterInstances.find(customParameterType.id);
            return this.customParameterInstanceCreatorFactory.create(initialInstance !== null && initialInstance !== void 0 ? initialInstance : this.customParameterInstanceFactory.createWithDefaultChoice(customParameterType));
        }));
        // Create CreatorScopeProvider with isSplit=true
        const creatorScopeProvider = new CreatorScopeProvider(this.fulfillmentInstructions, true, commonCustomParameterInstanceCreators);
        // Now create modifier creators with the scope provider
        const commonModifierInstanceCreators = IdentifiableMap.fromIterable("typeId", params.firstHalf.productType.nonDivisibleModifierTypes
            .union(params.secondHalf.productType.nonDivisibleModifierTypes)
            .objects.map((modifierType) => {
            const initialInstance = params.initialCommonModifierInstances.find(modifierType.id);
            const modifierSettings = modifierType.defaultSettings;
            return this.modifierInstanceCreatorFactory.create({
                modifierInstance: initialInstance !== null && initialInstance !== void 0 ? initialInstance : this.modifierInstanceFactory.createWithDefaultItems({
                    modifierType,
                    modifierSettings,
                }),
                modifierSettings,
                creatorScopeProvider,
            });
        }));
        const productTypePackagingFeeCreator = this.productTypePackagingFeeCreatorFactory.create(params.productPackagingFeeId);
        const state = new HalvesCreatorState({
            productCategory: params.productCategory,
            commonModifierInstanceCreators,
            commonCustomParameterInstanceCreators,
            firstHalf: this.productInstanceCreatorPartFactory.create(params.firstHalf, creatorScopeProvider),
            secondHalf: this.productInstanceCreatorPartFactory.create(params.secondHalf, creatorScopeProvider),
            creatorScopeProvider,
            productTypePackagingFeeCreator,
        });
        productTypePackagingFeeCreator.setProductInstance(state);
        return state;
    }
};
ProductInstanceCreatorStateFactory = __decorate([
    perRestaurant(),
    __param(0, inject(FulfillmentInstructionsToken)),
    __metadata("design:paramtypes", [typeof (_a = typeof FulfillmentInstructions !== "undefined" && FulfillmentInstructions) === "function" ? _a : Object, typeof (_b = typeof ModifierInstanceCreatorFactory !== "undefined" && ModifierInstanceCreatorFactory) === "function" ? _b : Object, typeof (_c = typeof ModifierInstanceFactory !== "undefined" && ModifierInstanceFactory) === "function" ? _c : Object, typeof (_d = typeof CustomParameterInstanceCreatorFactory !== "undefined" && CustomParameterInstanceCreatorFactory) === "function" ? _d : Object, typeof (_e = typeof CustomParameterInstanceFactory !== "undefined" && CustomParameterInstanceFactory) === "function" ? _e : Object, typeof (_f = typeof ProductInstanceCreatorPartFactory !== "undefined" && ProductInstanceCreatorPartFactory) === "function" ? _f : Object, typeof (_g = typeof ProductTypePackagingFeeCreatorFactory !== "undefined" && ProductTypePackagingFeeCreatorFactory) === "function" ? _g : Object])
], ProductInstanceCreatorStateFactory);
export { ProductInstanceCreatorStateFactory };
